import React, { useEffect, useState } from "react"
import { useParams } from "react-router-dom"

import {
	Box,
	Button,
	Grid,
	IconButton,
	Link,
	MenuItem,
	Paper,
	Select,
	Tooltip as MuiTooltip,
	Typography
} from "@material-ui/core"
import { GetApp as ExportIcon, InfoOutlined as InfoIcon } from "@material-ui/icons"
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers"
import DateFnsUtils from "@date-io/date-fns"
import { ptBR } from "date-fns/locale"
import { Eye as ViewPayloadIcon } from "lucide-react"
import {
	Bar,
	BarChart,
	CartesianGrid,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis
} from "recharts"
import { InfoDialog } from "letalkui"

import ActionDialog from "@/components/ActionDialog"
import Breadcrumb from "@/components/BreadcrumbNew"
import { TableColumn } from "@/components/DataTable"
import DataTableWithPagination from "@/components/DataTableWithPagination"
import JsonEditor from "@/components/JsonEditor/index"
import { AlertContainer, Loading, Portlet } from "@/components"
import WABAUsageStatementSkeleton from "@/skeletons/Admin/WABAUsageStatementSkeleton"
import { Alert } from "@material-ui/lab"
import { WABAUsageEntry } from "@/@integrations/WABA/protocols/channel"
import { WabaEventHistoryFormatted, sanitizeWabaEvent } from "@/@integrations/WABA/utils/wabaAccountInfo"
import WABAChannelService from "@/@integrations/WABA/services/WABAChannelService"
import ErrorHandlerService from "@/services/ErrorHandler"
import { ErrorType } from "@/hooks/useValidation"
import { DefaultWhereDataType } from "@/utils/pagination"
import { useGlobalStateStore } from "@/store/GlobalState"
import { colors } from "@/styles/colors"
import { downloadCsv } from "@/utils/file"
import { getDifferenceInDays, formatDateToYYYYMMDD, formatYYYYMMDDtoDDMM, formatYYYYMMDDtoDDMMYYYY } from "@/utils/time"
import { WABALinks } from "@/utils/link"

import CustomChartTooltip from "@/pages/Admin/WABAUsageStatement/components/CustomChartTooltip"
import { ChartClickState, ChartEntry, DateRange, Period, PeriodOption, UsageTableRow, WalletBalance } from "@/protocols/wabaUsageStatement"
import { calculateDateRange, getEndDateLimit } from "@/utils/wabaUsagePeriod"
import useStyles from "@/pages/Admin/WABAUsageStatement/styles"

const buildColumnHeader = (label: string, tooltip: string, classes: ReturnType<typeof useStyles>) => (): React.ReactElement => (
	<Grid item>
		<Grid container alignItems="center" spacing={1}>
			<Grid item>
				{label}
			</Grid>
			<Grid item className={classes.columnHeaderIconItem}>
				<MuiTooltip title={tooltip} placement="top">
					<InfoIcon className={classes.columnHeaderIcon} />
				</MuiTooltip>
			</Grid>
		</Grid>
	</Grid>
)

const WABAUsageStatement: React.FC = () => {
	const { inboxChannelId } = useParams<{ inboxChannelId: string }>()
	const classes = useStyles()
	const globalStateStore = useGlobalStateStore()
	const isAdminUser = globalStateStore.user?.extra_data?.is_admin_mode

	const [period, setPeriod] = useState<Period>(7)
	const [previousPeriod, setPreviousPeriod] = useState<Period>(7)
	const [dateRange, setDateRange] = useState<DateRange>(calculateDateRange(7))
	const [customFromDate, setCustomFromDate] = useState<Date | null>(new Date())
	const [customToDate, setCustomToDate] = useState<Date | null>(new Date())
	const [customRangeError, setCustomRangeError] = useState<string>("")
	const [isEndDateError, setIsEndDateError] = useState<boolean>(false)
	const [isCustomPeriodDialogOpen, setIsCustomPeriodDialogOpen] = useState<boolean>(false)
	const [usageData, setUsageData] = useState<WABAUsageEntry[]>([])
	const [walletBalance, setWalletBalance] = useState<WalletBalance | undefined>(undefined)
	const [loading, setLoading] = useState<boolean>(false)
	const [selectedDate, setSelectedDate] = useState<string | null>(null)
	const [eventsHistory, setEventsHistory] = useState<WabaEventHistoryFormatted[]>([])
	const [eventsTotalRows, setEventsTotalRows] = useState<number>(0)
	const [eventsPagination, setEventsPagination] = useState<DefaultWhereDataType>({ page: 0, rowsPerPage: 20 })
	const [isEventPayloadDialogOpen, setIsEventPayloadDialogOpen] = useState<boolean>(false)
	const [selectedEventPayload, setSelectedEventPayload] = useState<Record<string, unknown>>({})
	const [usagePagination, setUsagePagination] = useState<DefaultWhereDataType>({ page: 0, rowsPerPage: 20 })

	const channelId = Number(inboxChannelId)

	const fetchUsageAndWalletData = async (range: DateRange): Promise<void> => {
		setLoading(true)
		try {
			const [usageSummary, appData] = await Promise.all([
				WABAChannelService.getUsage(channelId, range.from, range.to),
				WABAChannelService.getAppData(channelId)
			])
			setUsageData(usageSummary?.usage ?? [])
			setWalletBalance(appData.walletBalance)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
		setLoading(false)
	}

	useEffect(() => {
		fetchUsageAndWalletData(dateRange)
	}, [dateRange.from, dateRange.to])

	const fetchEventsHistory = async (pagination: DefaultWhereDataType): Promise<void> => {
		try {
			const result = await WABAChannelService.getWabaAppEventsHistory(channelId, pagination)
			const formattedEvents = result.events.map(sanitizeWabaEvent)
			setEventsHistory(formattedEvents)
			setEventsTotalRows(result.totalRows)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
	}

	useEffect(() => {
		fetchEventsHistory(eventsPagination)
	}, [eventsPagination.page, eventsPagination.rowsPerPage])

	const handleEventsPaginationChange = (data: Partial<DefaultWhereDataType>): void => {
		setEventsPagination((prev) => ({ ...prev, ...data }))
	}

	const handleUsagePaginationChange = (data: Partial<DefaultWhereDataType>): void => {
		setUsagePagination((prev) => ({ ...prev, ...data }))
	}

	const handleOpenEventPayloadDialog = (payload: Record<string, unknown>): void => {
		setSelectedEventPayload(payload)
		setIsEventPayloadDialogOpen(true)
	}

	const handleCloseEventPayloadDialog = (): void => {
		setIsEventPayloadDialogOpen(false)
		setSelectedEventPayload({})
	}

	const handlePeriodSelectChange = (event: React.ChangeEvent<{ value: unknown }>): void => {
		const newPeriod = event.target.value as Period
		if (newPeriod === "custom") {
			setPreviousPeriod(period)
			setPeriod("custom")
			setIsCustomPeriodDialogOpen(true)
			return
		}
		setPeriod(newPeriod)
		setCustomRangeError("")
		setDateRange(calculateDateRange(newPeriod))
	}

	const handleCustomPeriodApply = (): void => {
		if (!customFromDate || !customToDate) {
			setCustomRangeError("Informe as datas de início e fim.")
			return
		}
		const fromStr = formatDateToYYYYMMDD(customFromDate)
		const toStr = formatDateToYYYYMMDD(customToDate)
		const diff = getDifferenceInDays(new Date(toStr), new Date(fromStr))
		if (diff < 0) {
			setCustomRangeError("A data de início não pode ser maior que a data de fim.")
			return
		}
		setCustomRangeError("")
		setDateRange({ from: fromStr, to: toStr })
		setIsCustomPeriodDialogOpen(false)
	}

	const handleCustomPeriodDialogClose = (): void => {
		setPeriod(previousPeriod)
		setCustomRangeError("")
		setIsEndDateError(false)
		setIsCustomPeriodDialogOpen(false)
	}

	const handleCustomFromChange = (date: Date | null): void => {
		setCustomFromDate(date)
		setIsEndDateError(false)
	}

	const handleCustomToChange = (date: Date | null): void => {
		setCustomToDate(date)
	}

	const handleCustomToError = (reason: string | null): void => {
		setIsEndDateError(Boolean(reason))
	}

	const handleChartBarClick = (chartState: ChartClickState): void => {
		const entry = chartState.activePayload?.[0]?.payload
		if (!entry) {
			return
		}
		if (entry.originalDate === selectedDate) {
			setSelectedDate(null)
			return
		}
		setSelectedDate(entry.originalDate)
		const rowElement = document.getElementById(`row-${entry.originalDate}`)
		rowElement?.scrollIntoView({ behavior: "smooth", block: "nearest" })
	}

	const getUsageTableRowProps = (row: UsageTableRow): React.HTMLAttributes<HTMLTableRowElement> => ({
		id: `row-${row.id}`,
		className: row.id === selectedDate ? classes.selectedRow : classes.normalRow
	})

	const totalFees = usageData.reduce((acc, entry) => acc + Number(entry.totalFees), 0)
	const totalMsg = usageData.reduce(
		(acc, entry) => acc + entry.incomingMsg + entry.marketing + entry.mmLiteMarketing + entry.service + entry.utility,
		0
	)

	const walletBalanceDisplay = walletBalance
		? `$${walletBalance.currentBalance} ${walletBalance.currency}`
		: "—"

	const chartData: ChartEntry[] = usageData.map((entry) => ({
		date: formatYYYYMMDDtoDDMM(entry.date),
		originalDate: entry.date,
		cost: Number(entry.totalFees) || null,
		totalMsg: entry.incomingMsg + entry.marketing + entry.mmLiteMarketing + entry.service + entry.utility
	}))

	const hasChartData = chartData.length > 0

	const periodOptions: PeriodOption[] = [
		{ label: "Ontem", value: "yesterday" },
		{ label: "Últimos 7 dias", value: 7 },
		{ label: "Últimos 30 dias", value: 30 },
		{ label: "Personalizado", value: "custom" }
	]

	const usageTableColumns: TableColumn<UsageTableRow>[] = [
		{ field: "date", label: "Data" },
		{
			field: "mmLiteMarketing",
			label: "Marketing",
			headRender: buildColumnHeader("Marketing", "Total de templates de marketing enviados via Marketing Messages Lite (MM Lite)", classes)
		},
		{
			field: "service",
			label: "Mensagens de Serviço",
			headRender: buildColumnHeader("Mensagens de Serviço", "Total de mensagens de sessão, incluindo mensagens recebidas de clientes (que abrem ou renovam a janela de 24h) e respostas de serviço enviadas dentro desse período.", classes)
		},
		{
			field: "utility",
			label: "Utilidade",
			headRender: buildColumnHeader("Utilidade", "Total de templates de utilidade", classes)
		}
	]

	const usageTableData: UsageTableRow[] = [...usageData].reverse().map((entry) => ({
		id: entry.date,
		date: formatYYYYMMDDtoDDMMYYYY(entry.date),
		mmLiteMarketing: String(entry.mmLiteMarketing ?? 0),
		service: String(entry.incomingMsg + entry.service),
		utility: String(entry.utility),
		dailyCost: `$${Number(entry.totalFees).toFixed(2)}`
	}))

	const handleExportToCsv = (): void => {
		const headers = ["Data", "Marketing", "Mensagens de Serviço", "Utilidade", "Consumo Diário (USD)"]
		const rows = usageData.map((entry) => [
			entry.date,
			String(entry.mmLiteMarketing ?? 0),
			String(entry.incomingMsg + entry.service),
			String(entry.utility),
			String(Number(entry.totalFees))
		])
		downloadCsv(headers, rows, `extrato-waba-${dateRange.from}-${dateRange.to}.csv`)
	}

	const eventTypeColumn: TableColumn<WabaEventHistoryFormatted["history"]> = {
		field: "eventTypeLabel",
		label: "Tipo de evento"
	}

	const eventSubTypeColumn: TableColumn<WabaEventHistoryFormatted["history"]> = {
		field: "eventSubTypeLabel",
		label: "Subtipo de evento"
	}

	const eventCreatedAtColumn: TableColumn<WabaEventHistoryFormatted["history"]> = {
		field: "createdAt",
		label: "Data/hora"
	}

	const adminPayloadActionColumn: TableColumn<WabaEventHistoryFormatted["history"]> = {
		field: "payload",
		label: "Ação",
		align: "center",
		rowRender: (payload) => (
			<Grid container justifyContent="center" alignItems="center" spacing={1}>
				<Grid item>
					<IconButton
						size="small"
						onClick={() => handleOpenEventPayloadDialog(payload as Record<string, unknown>)}
						color="primary"
					>
						<ViewPayloadIcon size={16} />
					</IconButton>
				</Grid>
				<Grid item>
					<Typography
						variant="caption"
						color="primary"
						style={{ cursor: "pointer" }}
						onClick={() => handleOpenEventPayloadDialog(payload as Record<string, unknown>)}
					>
						Ver mais
					</Typography>
				</Grid>
			</Grid>
		)
	}

	const baseEventHistoryColumns: TableColumn<WabaEventHistoryFormatted["history"]>[] = [
		eventTypeColumn,
		eventSubTypeColumn,
		eventCreatedAtColumn
	]

	const adminEventHistoryColumns: TableColumn<WabaEventHistoryFormatted["history"]>[] = [
		...baseEventHistoryColumns,
		adminPayloadActionColumn
	]

	const visibleEventHistoryColumns = isAdminUser ? adminEventHistoryColumns : baseEventHistoryColumns

	return (
		<>
			<Grid container direction="column" spacing={5} className={classes.container}>
				<Grid item>
					<Breadcrumb
						data={[
							{ name: "Extrato de consumo WABA", pathname: `/admin/waba/channel/${inboxChannelId}/usage-statement` }
						]}
					/>
				</Grid>

				<Grid item>
					<Grid container direction="column" spacing={1}>
						<Grid item>
							<Typography variant="h5" className={classes.titleText}>
								Extrato de consumo
							</Typography>
						</Grid>
						<Grid item>
							<Typography className={classes.subtitleText}>
								Acompanhe o consumo de mensagens e os custos associados ao seu canal WhatsApp Business no período selecionado, com dados fornecidos pela Gupshup.
							</Typography>
						</Grid>
					</Grid>
					<div className={classes.walletBalanceBadge}>
						<Typography className={classes.walletBalanceBadgeLabel}>
							Saldo de créditos atual:
						</Typography>
						<Typography className={classes.walletBalanceBadgeValue}>
							{walletBalanceDisplay}
						</Typography>
					</div>
				</Grid>

				<Grid item>
					<AlertContainer alertType="info" title="Consulta de valores por template">
						<Typography className={classes.alertInfoText}>
							Para consultar os valores cobrados por template, acesse a página de preços da Gupshup{" "}
							<Link href={WABALinks.gupshupPricing} target="_blank" style={{ cursor: "pointer" }}>
								clicando aqui
							</Link>
							.
						</Typography>
					</AlertContainer>
				</Grid>

				<Grid item>
					<Grid container justify="space-between" alignItems="stretch">
						<Grid item>
							<Select
								variant="outlined"
								value={period}
								onChange={handlePeriodSelectChange}
								MenuProps={{
									getContentAnchorEl: null,
									anchorOrigin: { vertical: "bottom", horizontal: "left" },
									transformOrigin: { vertical: "top", horizontal: "left" }
								}}
							>
								{periodOptions.map((option) => (
									<MenuItem
										key={option.value}
										value={option.value}
										onClick={option.value === "custom" ? () => setIsCustomPeriodDialogOpen(true) : undefined}
									>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</Grid>
						<Grid item>
							<MuiTooltip title="Exportar relatório de dados de consumo do período filtrado" placement="top">
								<span className={classes.exportButtonWrapper}>
									<Button
										variant="outlined"
										color="primary"
										className={classes.exportButtonStretch}
										startIcon={<ExportIcon />}
										onClick={handleExportToCsv}
										disabled={usageData.length === 0}
									>
										Exportar
									</Button>
								</span>
							</MuiTooltip>
						</Grid>
					</Grid>
				</Grid>

				<Grid item xs={12}>
					<Loading loading={loading} customLoadingElement={<WABAUsageStatementSkeleton />}>
						<Grid container direction="column" spacing={5}>
							<Grid item>
								<div className={classes.cardsRow}>
									<div className={`${classes.summaryCard} ${classes.summaryCardHighlighted}`}>
										<Typography className={classes.summaryCardLabel}>
											Total gasto no período
										</Typography>
										<Typography className={classes.summaryCardValue}>
											${parseFloat(totalFees.toFixed(6))}
										</Typography>
									</div>

									<div className={classes.summaryCard}>
										<Typography className={classes.summaryCardLabel}>
											Total de mensagens no período
										</Typography>
										<Typography className={classes.summaryCardValue}>
											{totalMsg}
										</Typography>
									</div>
								</div>
							</Grid>

							<Grid item>
								<div className={classes.chartSectionTitleContainer}>
									<Typography className={classes.chartSectionTitle}>
										Consumo diário
									</Typography>
									<MuiTooltip
										title="Os dados exibidos cobrem até o dia anterior. As informações de hoje ainda não são fornecidas pela Gupshup. Além disso, só aparecem no gráfico os dias em que houve consumo de créditos ou troca de mensagens."
										placement="top"
									>
										<InfoIcon htmlColor={colors.gray[400]} className={classes.chartSectionInfoIcon} />
									</MuiTooltip>
								</div>
								<Typography className={classes.chartSectionSubtitle}>
									Custo total por dia (USD). Clique em qualquer ponto do gráfico para destacar a linha correspondente na tabela.
								</Typography>
								<div className={classes.chartSection}>
									<Box mt={2}>
										{hasChartData ? (
											<ResponsiveContainer width="99%" height={260}>
												<BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 8 }} onClick={handleChartBarClick} style={{ cursor: "pointer" }}>
													<CartesianGrid strokeDasharray="3 3" vertical={false} stroke={colors.gray[100]} />
													<XAxis
														dataKey="date"
														tick={{ fontSize: 12, fill: colors.gray[500] }}
														axisLine={false}
														tickLine={false}
													/>
													<YAxis
														tickFormatter={(value: number) => `$${Number(value).toFixed(2)}`}
														tick={{ fontSize: 12, fill: colors.gray[500] }}
														axisLine={false}
														tickLine={false}
														width={70}
													/>
													<Tooltip
														content={<CustomChartTooltip />}
														cursor={{ fill: colors.purple[50] }}
													/>
													<Bar
														dataKey="cost"
														fill={colors.purple[500]}
														radius={[4, 4, 0, 0]}
														maxBarSize={60}
													/>
												</BarChart>
											</ResponsiveContainer>
										) : (
											<div className={classes.emptyState}>
												<Typography variant="body2">
													Nenhum dado encontrado para o período selecionado
												</Typography>
											</div>
										)}
									</Box>
								</div>
							</Grid>

							<Grid item>
								<Typography className={classes.tableSectionTitle}>
									Mensagens por categoria
								</Typography>
								<Portlet>
									<Paper elevation={0}>
										<DataTableWithPagination
											columns={usageTableColumns}
											data={usageTableData.slice(usagePagination.page * usagePagination.rowsPerPage, (usagePagination.page + 1) * usagePagination.rowsPerPage)}
											page={usagePagination.page}
											rowsPerPage={usagePagination.rowsPerPage}
											totalRows={usageTableData.length}
											onPaginationChange={handleUsagePaginationChange}
											getRowProps={getUsageTableRowProps}
										/>
									</Paper>
								</Portlet>
							</Grid>

							{eventsHistory.length > 0 && (
								<Grid item>
									<Typography className={classes.tableSectionTitle}>
										Histórico de Eventos
									</Typography>
									<Portlet>
										<Paper elevation={0}>
											<DataTableWithPagination
												columns={visibleEventHistoryColumns}
												data={eventsHistory.map((event) => event.history)}
												page={eventsPagination.page}
												rowsPerPage={eventsPagination.rowsPerPage}
												totalRows={eventsTotalRows}
												onPaginationChange={handleEventsPaginationChange}
											/>
										</Paper>
									</Portlet>
								</Grid>
							)}
						</Grid>
					</Loading>
				</Grid>
			</Grid>

			<ActionDialog
				title="Selecionar período personalizado"
				openDialog={isCustomPeriodDialogOpen}
				onSave={handleCustomPeriodApply}
				onClose={handleCustomPeriodDialogClose}
				saveText="Aplicar"
				cancelText="Cancelar"
				fullWidth
				maxWidth="sm"
				saveButtonDisabled={isEndDateError}
			>
				<Grid container direction="column" spacing={3}>
					<Grid item xs={12}>
						<Typography>
							Selecione o período desejado para visualizar os dados de consumo.
						</Typography>
					</Grid>
					<Grid item xs={12}>
						<Grid container spacing={2}>
							<Grid item xs={6}>
								<Grid container direction="column" spacing={1}>
									<Grid item>
										<Typography variant="subtitle1">
											Data de início:
										</Typography>
									</Grid>
									<Grid item>
										<MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
											<DateTimePicker
												ampm={false}
												format="dd/MM/yyyy HH:mm"
												animateYearScrolling
												views={["year", "month", "date", "hours", "minutes"]}
												variant="inline"
												inputVariant="outlined"
												fullWidth
												autoOk
												value={customFromDate}
												onChange={handleCustomFromChange}
												maxDate={new Date()}
											/>
										</MuiPickersUtilsProvider>
									</Grid>
								</Grid>
							</Grid>
							<Grid item xs={6}>
								<Grid container direction="column" spacing={1}>
									<Grid item>
										<Typography variant="subtitle1">
											Data final:
										</Typography>
									</Grid>
									<Grid item>
										<MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
											<DateTimePicker
												ampm={false}
												format="dd/MM/yyyy HH:mm"
												animateYearScrolling
												views={["year", "month", "date", "hours", "minutes"]}
												variant="inline"
												inputVariant="outlined"
												fullWidth
												autoOk
												value={customToDate}
												onChange={handleCustomToChange}
												minDate={customFromDate || undefined}
												maxDate={getEndDateLimit()}
												onError={handleCustomToError}
											/>
										</MuiPickersUtilsProvider>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Grid item xs={12}>
						{customRangeError && (
							<Alert severity="error" icon={false}>
								{customRangeError}
							</Alert>
						)}
					</Grid>
				</Grid>
			</ActionDialog>

			{isAdminUser && (
				<InfoDialog
					open={isEventPayloadDialogOpen}
					onClose={handleCloseEventPayloadDialog}
					title="Payload do evento"
				>
					<JsonEditor
						mode="json"
						width="484px"
						height="300px"
						defaultValue={JSON.stringify(selectedEventPayload, null, 2)}
					/>
				</InfoDialog>
			)}
		</>
	)
}

export default WABAUsageStatement
